import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AutoTopupSetting,
  GetAutoTopupSettingsResponse,
  UpsertAutoTopupRequest,
  UpsertAutoTopupResponse,
  TopUpProduct,
} from '@dreamai/types';
import api from '../../api';
import './auto-topup-section.scss';

interface AutoTopupSectionProps {
  topUpProducts: TopUpProduct[];
  hasSubscription: boolean;
}

const LIMIT_PRESETS: Record<string, number[]> = {
  GBP: [30, 60, 150],
  USD: [40, 80, 200],
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
};

const AutoTopupSection: FC<AutoTopupSectionProps> = ({
  topUpProducts,
  hasSubscription,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  const [settings, setSettings] = useState<AutoTopupSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [limit, setLimit] = useState<number | 'custom'>(30);
  const [customValue, setCustomValue] = useState('');
  const [customEditing, setCustomEditing] = useState(false);

  const creditTopUp = topUpProducts.find((t) => t.creditType === 'credit');
  const currency = creditTopUp?.currency?.toUpperCase() || 'GBP';
  const presets = LIMIT_PRESETS[currency] || LIMIT_PRESETS.GBP;
  const symbol = CURRENCY_SYMBOLS[currency] || `${currency} `;

  const creditSetting = settings.find((s) => s.creditType === 'credit');
  const enabled = creditSetting?.enabled ?? false;

  // Refs to avoid stale closures in callbacks
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const limitRef = useRef(limit);
  limitRef.current = limit;
  const customValueRef = useRef(customValue);
  customValueRef.current = customValue;

  const getCapValue = useCallback(() => {
    const l = limitRef.current;
    return l === 'custom'
      ? parseFloat(customValueRef.current) || presets[0]
      : l;
  }, [presets]);

  const fetchSettings = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetAutoTopupSettingsResponse>(
        '/credits/auto-topup',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSettings(res.data.settings);

      const setting = res.data.settings.find((s) => s.creditType === 'credit');
      if (setting) {
        const cap = setting.monthlyBudgetCap;
        if (presets.includes(cap)) {
          setLimit(cap);
        } else {
          setLimit('custom');
          setCustomValue(String(cap));
          setCustomEditing(false);
        }
      } else {
        setLimit(presets[0]);
      }
    } catch (err) {
      console.error('Error fetching auto-topup settings:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, presets]);

  useEffect(() => {
    if (hasSubscription) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [hasSubscription, fetchSettings]);

  const upsertSetting = useCallback(
    async (nowEnabled: boolean, budgetCap: number) => {
      if (!creditTopUp) return;

      setSaving(true);
      try {
        const token = await getAccessTokenSilently();
        const body: UpsertAutoTopupRequest = {
          creditType: 'credit',
          enabled: nowEnabled,
          monthlyBudgetCap: budgetCap,
          balanceThreshold: 1000,
          topupPriceId: creditTopUp.priceId,
          topupQuantity: 1,
        };
        await api.put<UpsertAutoTopupResponse>('/credits/auto-topup', body, {
          headers: { Authorization: `Bearer ${token}` },
        });
        await fetchSettings();
      } catch (err) {
        console.error('Error saving auto-topup setting:', err);
      } finally {
        setSaving(false);
      }
    },
    [getAccessTokenSilently, creditTopUp, fetchSettings],
  );

  const handleToggle = useCallback(() => {
    const current = settingsRef.current.find((s) => s.creditType === 'credit');
    const nowEnabled = current?.enabled ?? false;
    upsertSetting(!nowEnabled, getCapValue());
  }, [upsertSetting, getCapValue]);

  const handleLimitChange = useCallback(
    (value: number | 'custom') => {
      setLimit(value);
      if (value === 'custom') {
        setCustomEditing(true);
      } else {
        const current = settingsRef.current.find(
          (s) => s.creditType === 'credit',
        );
        if (current?.enabled) {
          upsertSetting(true, value);
        }
      }
    },
    [upsertSetting],
  );

  const handleCustomSubmit = useCallback(() => {
    const val = parseFloat(customValueRef.current);
    if (val > 0) {
      setCustomEditing(false);
      const current = settingsRef.current.find(
        (s) => s.creditType === 'credit',
      );
      if (current?.enabled) {
        upsertSetting(true, val);
      }
    }
  }, [upsertSetting]);

  if (!hasSubscription || loading) return null;

  const formatIncrement = (product: TopUpProduct | undefined) => {
    if (!product) return '';
    const withVat = Math.round(product.unitAmount * 1.2 * 100) / 100;
    const sym =
      CURRENCY_SYMBOLS[product.currency.toUpperCase()] ||
      `${product.currency} `;
    return `${sym}${withVat.toFixed(2).replace(/\.00$/, '')}`;
  };

  return (
    <div className='plan-card autotopup-card'>
      <div className='plan-card-header'>
        <h2 className='plan-card-title'>Automatically top-up</h2>
        <p className='plan-card-subtitle'>
          Automatically top-up your account when you run low on credits. Set a
          limit to keep in control.
        </p>
      </div>

      {creditTopUp && (
        <div className='autotopup-toggle-row'>
          <button
            className={`toggle-switch${enabled ? ' on' : ''}`}
            onClick={handleToggle}
            disabled={saving}
            aria-label='Enable automatic top-ups'
          >
            <span className='toggle-knob' />
          </button>
          <div className='autotopup-toggle-info'>
            <p className='autotopup-toggle-label'>Enable automatic top-ups</p>
            <p className='autotopup-toggle-desc'>
              Topped up in blocks of{' '}
              {creditTopUp.creditsPerUnit.toLocaleString('en-GB')} credits (
              {formatIncrement(creditTopUp)})
            </p>
          </div>
        </div>
      )}

      {enabled && (
        <div className='autotopup-limit-row'>
          <span className='autotopup-limit-label'>Monthly top-up limit</span>
          <div className='autotopup-segmented'>
            {presets.map((preset) => (
              <button
                key={preset}
                className={`segment${limit === preset ? ' selected' : ''}`}
                onClick={() => handleLimitChange(preset)}
                disabled={saving}
              >
                {symbol}
                {preset}
              </button>
            ))}
            {limit === 'custom' && customEditing ? (
              <div className='segment custom-input selected'>
                <span className='custom-prefix'>{symbol}</span>
                <input
                  type='number'
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  onBlur={handleCustomSubmit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCustomSubmit();
                  }}
                  autoFocus
                  min='1'
                />
              </div>
            ) : limit === 'custom' && customValue ? (
              <button
                className='segment selected'
                onClick={() => setCustomEditing(true)}
                disabled={saving}
              >
                {symbol}
                {customValue}
              </button>
            ) : (
              <button
                className='segment'
                onClick={() => handleLimitChange('custom')}
                disabled={saving}
              >
                Custom
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoTopupSection;
